import findIndex from 'lodash/findIndex';
import findLastIndex from 'lodash/findLastIndex';
import { v4 as uuid } from 'uuid';
import {
  IAskAiChatHistoryMessage,
  IAskAiChatMessage,
  IAskAiProcessingPayload,
  MessageRole,
  MessageStatus,
  TurnStatus,
} from '@/creditflowresearch/domain/vo/AskAiChat';

type Messages = IAskAiChatMessage[];

interface IQuestionRemoval {
  question: string,
  messages: Messages,
}

export const EMPTY_ANSWER_MESSAGE = 'No answer was produced. Try rephrasing your request.';

const NO_STAGE = { stage: undefined, progress: undefined };

const update = (
  messages: Messages,
  shouldUpdate: (message: IAskAiChatMessage) => boolean,
  patch: (message: IAskAiChatMessage) => Partial<IAskAiChatMessage>,
): Messages => {
  return messages.map((message) => (shouldUpdate(message) ? { ...message, ...patch(message) } : message));
};

export const isInFlight = ({ status }: IAskAiChatMessage): boolean => {
  return status === MessageStatus.Fetching || status === MessageStatus.Streaming;
};

const isFinal = ({ status }: IAskAiChatMessage): boolean => {
  return status === MessageStatus.Completed || status === MessageStatus.Error;
};

const getTurnStatus = (metadata: unknown): TurnStatus | undefined => {
  if (!metadata || typeof metadata !== 'object' || !('status' in metadata)) {
    return undefined;
  }

  return Object.values(TurnStatus).find((status) => status === metadata.status);
};

export const toMessage = (message: IAskAiChatHistoryMessage): IAskAiChatMessage => {
  const isEmpty = message.role === MessageRole.Assistant && !message.content;

  return {
    id: message.id,
    role: message.role,
    content: message.content,
    createdAt: message.createdAt,
    status: isEmpty ? MessageStatus.Error : MessageStatus.Completed,
    errorMessage: isEmpty ? EMPTY_ANSWER_MESSAGE : undefined,
    turnStatus: getTurnStatus(message.metadata),
  };
};

export const appendQuestion = (messages: Messages, text: string, placeholderId: string): Messages => {
  const createdAt = new Date().toISOString();

  return [
    ...messages,
    { id: uuid(), role: MessageRole.User, content: text, createdAt, status: MessageStatus.Completed },
    { id: placeholderId, role: MessageRole.Assistant, content: '', createdAt, status: MessageStatus.Fetching },
  ];
};

export const renamePlaceholder = (messages: Messages, placeholderId: string, messageId: string): Messages => {
  return update(messages, ({ id }) => id === placeholderId, () => ({ id: messageId }));
};

export const addAssistantMessage = (messages: Messages, id: string): Messages => {
  return [
    ...messages,
    { id, role: MessageRole.Assistant, content: '', createdAt: new Date().toISOString(), status: MessageStatus.Fetching },
  ];
};

export const applyProcessing = (
  messages: Messages,
  targetId: string,
  { stage, label, progress }: IAskAiProcessingPayload,
): Messages => {
  return update(
    messages,
    ({ id }) => id === targetId,
    ({ stageLabel }) => ({ stage, progress, stageLabel: label || stageLabel }),
  );
};

export const applyAnswer = (messages: Messages, messageId: string, content: string): Messages => {
  return update(
    messages,
    ({ id }) => id === messageId,
    () => ({ content, status: MessageStatus.Streaming, ...NO_STAGE }),
  );
};

export const applyError = (messages: Messages, messageId: string, errorMessage: string): Messages => {
  return update(messages, ({ id }) => id === messageId, () => ({ status: MessageStatus.Error, errorMessage }));
};

export const applyDone = (messages: Messages, turnIds: Set<string>): Messages => {
  return update(
    messages,
    (message) => turnIds.has(message.id) && message.status !== MessageStatus.Error,
    ({ content }) => {
      if (content) {
        return { status: MessageStatus.Completed, ...NO_STAGE };
      }

      return { status: MessageStatus.Error, errorMessage: EMPTY_ANSWER_MESSAGE, ...NO_STAGE };
    },
  );
};

export const applyCancelled = (messages: Messages, turnIds: Set<string>): Messages => {
  return update(
    messages,
    (message) => turnIds.has(message.id) && !isFinal(message),
    () => ({ status: MessageStatus.Completed, turnStatus: TurnStatus.Cancelled, ...NO_STAGE }),
  );
};

export const applyFailure = (
  messages: Messages,
  placeholderId: string | null,
  errorMessage: string,
): Messages => {
  return update(
    messages,
    (message) => isInFlight(message) || message.id === placeholderId,
    () => ({ status: MessageStatus.Error, errorMessage }),
  );
};

const isQuestion = ({ role }: IAskAiChatMessage): boolean => {
  return role === MessageRole.User;
};

export const getTurnEndStatuses = (messages: Messages): Map<string, TurnStatus> => {
  const statuses = new Map<string, TurnStatus>();

  messages.forEach(({ id, turnStatus }, index) => {
    const next = messages[index + 1];

    if (turnStatus && (!next || isQuestion(next))) {
      statuses.set(id, turnStatus);
    }
  });

  return statuses;
};

export const findQuestionIndex = (messages: Messages, messageId: string): number => {
  const index = messages.findIndex(({ id }) => id === messageId);

  if (index === -1) {
    return -1;
  }

  return findLastIndex(messages, isQuestion, index);
};

export const removeQuestionAndAnswers = (messages: Messages, messageId: string): IQuestionRemoval | null => {
  const start = findQuestionIndex(messages, messageId);

  if (start === -1) {
    return null;
  }

  const nextQuestion = findIndex(messages, isQuestion, start + 1);
  const end = nextQuestion === -1 ? messages.length : nextQuestion;

  return {
    question: messages[start].content,
    messages: [...messages.slice(0, start), ...messages.slice(end)],
  };
};
